import { type DBSchema, type IDBPDatabase, openDB } from "idb";

/**
 * Queue item for IndexedDB storing attachments inline
 * Generic type T allows different data structures per queue item
 */
export interface QueueItemDB<T = Record<string, any>> {
  id: string;
  timestamp: number;
  status: "pending" | "sending" | "sent" | "error";
  retryCount: number;
  data: T;
  error?: string;
}

/**
 * IndexedDB schema for queue items
 */
interface QueueDB extends DBSchema {
  // Store for queued items
  formQueue: {
    key: string;
    value: QueueItemDB;
  };
}

/**
 * IndexedDB Manager for Queue
 */
class IndexedDBManager {
  private dbName = "offlineQueueDB";
  private version = 4;
  private db: IDBPDatabase<QueueDB> | null = null;

  /**
   * Initialize and open the database
   */
  async init(): Promise<void> {
    this.db = await openDB<QueueDB>(this.dbName, this.version, {
      upgrade(db) {
        const storeNames = Array.from(db.objectStoreNames) as string[];

        if (storeNames.includes("formQueue")) {
          db.deleteObjectStore("formQueue");
        }

        db.createObjectStore("formQueue", {
          keyPath: "id",
        });
      },
    });
    console.log("✅ IndexedDB initialized (version 3)");
  }

  /**
   * Ensure database is initialized
   */
  private async ensureDB(): Promise<IDBPDatabase<QueueDB>> {
    if (!this.db) {
      await this.init();
    }
    return this.db!;
  }

  // ==================== Queue Item Operations ====================

  /**
   * Save queue item to IndexedDB
   */
  async saveQueueItem(item: QueueItemDB): Promise<void> {
    const db = await this.ensureDB();
    await db.put("formQueue", item);
    console.log("✅ Queue item saved:", item.id);
  }

  /**
   * Get single queue item
   */
  async getQueueItem<T extends Record<string, any> = Record<string, any>>(
    id: string,
  ): Promise<QueueItemDB<T> | undefined> {
    const db = await this.ensureDB();
    const item = await db.get("formQueue", id);
    if (!item) {
      return undefined;
    }

    return item as QueueItemDB<T>;
  }

  /**
   * Get all queue items
   */
  async getAllQueueItems<
    T extends Record<string, any> = Record<string, any>,
  >(): Promise<QueueItemDB<T>[]> {
    const db = await this.ensureDB();
    const items = await db.getAll("formQueue");
    return items.sort((a, b) => a.timestamp - b.timestamp) as QueueItemDB<T>[];
  }

  /**
   * Delete queue item
   */
  async deleteQueueItem(id: string): Promise<void> {
    const db = await this.ensureDB();
    await db.delete("formQueue", id);
    console.log("🗑️ Queue item deleted:", id);
  }

  /**
   * Get queue size
   */
  async getQueueSize(): Promise<number> {
    const db = await this.ensureDB();
    return await db.count("formQueue");
  }

  // ==================== Utility Methods ====================

  /**
   * Clear all sent items (cleanup)
   */
  async clearSentItems(): Promise<number> {
    const sentItems = await this.getAllQueueItems();

    for (const item of sentItems.filter(
      (queueItem) => queueItem.status === "sent",
    )) {
      await this.deleteQueueItem(item.id);
    }

    console.log(`🧹 Cleared ${sentItems.length} sent items`);
    return sentItems.length;
  }
}

// Export singleton instance
export const indexedDBManager = new IndexedDBManager();
